import { useEffect, useState } from "react";
import { Check, Copy } from "lucide-react";

interface KeyOnceModalProps {
  gatewayKey: string;
  onDismiss: () => void;
}

/** Shows a freshly rotated gateway key exactly once. The key lives only in
 * the parent's component state — never in the query cache, localStorage, or
 * logs — and the parent drops it when this modal is dismissed. */
export function KeyOnceModal({ gatewayKey, onDismiss }: KeyOnceModalProps) {
  const [copied, setCopied] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);

  // No Escape-to-close: dismissal must be the explicit button so users don't
  // lose the key to a reflexive keypress.
  useEffect(() => {
    const block = (e: KeyboardEvent) => {
      if (e.key === "Escape") e.preventDefault();
    };
    document.addEventListener("keydown", block);
    return () => document.removeEventListener("keydown", block);
  }, []);

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(gatewayKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable (permissions) — user can still select the text.
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
      role="dialog"
      aria-modal="true"
      aria-label="Your new gateway key"
    >
      <div className="w-full max-w-lg rounded-lg border border-line bg-surface p-6 shadow-xl space-y-4">
        <h3 className="text-base font-semibold text-fg">Your new gateway key</h3>
        <p className="text-sm text-fg-muted">
          Copy it now — for security reasons it{" "}
          <span className="font-medium text-fg">cannot be shown again</span>. If you
          lose it, rotate to get a new one.
        </p>

        <div className="flex items-center gap-2 rounded-md border border-line bg-canvas px-3 py-2.5">
          <code className="flex-1 break-all font-mono text-xs text-fg">{gatewayKey}</code>
          <button
            type="button"
            onClick={handleCopy}
            className="shrink-0 rounded-md border border-line px-2.5 py-1.5 text-xs text-fg-muted transition-colors hover:bg-surface-2 hover:text-fg"
          >
            {copied ? (
              <span className="flex items-center gap-1 text-safe-green">
                <Check size={13} /> Copied
              </span>
            ) : (
              <span className="flex items-center gap-1">
                <Copy size={13} /> Copy
              </span>
            )}
          </button>
        </div>

        <label className="flex items-start gap-2 text-sm text-fg-muted">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            className="mt-0.5"
          />
          I've saved my key somewhere safe.
        </label>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={onDismiss}
            disabled={!acknowledged}
            className="rounded-md bg-electric-blue px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-electric-blue/90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
